<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  createApp,
  h,
  watch,
} from 'vue';
import { useRuntimeConfig, useNuxtApp } from '#app';
import { MAP_STYLES, ICON_ACTIVE, ICON_DEFAULT } from '@/configs/map';
import { useGlobalStore } from '@/stores/global';
import PointsSlider from '@/components/Maps/PointsSlider.vue';
import MapPointCard from '@/components/cards/MapPointCard.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isCarPage: {
    type: Boolean,
    default: false,
  },
});

const globalStore = useGlobalStore();
const googleMapsApiKey = computed(() => globalStore.googleMapsApiKey);

const nuxtApp = useNuxtApp();

const TEMPLATE = computed(() => props.data.template || '');
const centers = computed(() => props.data.items || []);
const mapEl = ref(null);
let map = null;
let infoWindow = null;
let markers = [];
let activeMarkerIndex = -1;

let infoApp = null;
let infoContainer = null;

function unmountInfoCard() {
  if (infoApp) {
    try {
      infoApp.unmount();
    } catch {
      console.log('error');
    }
    infoApp = null;
  }
  infoContainer = null;
}

let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (gmapsPromise) return gmapsPromise;

  gmapsPromise = new Promise((resolve, reject) => {
    const id = 'gmaps-script';
    if (document.getElementById(id)) {
      const wait = () =>
        window.google?.maps ? resolve() : setTimeout(wait, 50);
      return wait();
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gmapsPromise;
}
function initMap() {
  if (!mapEl.value || !window.google?.maps) return;
  const first = centers.value?.[0];
  const firstLat = Number(first?.marker?.lat);
  const firstLng = Number(first?.marker?.lng);
  const initialCenter =
    !Number.isNaN(firstLat) && !Number.isNaN(firstLng)
      ? { lat: firstLat, lng: firstLng }
      : { lat: 50.4501, lng: 30.5234 };
  map = new window.google.maps.Map(mapEl.value, {
    center: initialCenter,
    zoom: 11,
    styles: MAP_STYLES,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
  });
  infoWindow = new window.google.maps.InfoWindow();
  window.google.maps.event.addListener(infoWindow, 'closeclick', () => {
    unmountInfoCard();
    resetActiveMarker();
  });

  map.addListener('click', () => {
    infoWindow.close();
    unmountInfoCard();
    resetActiveMarker();
  });
}

function getAutoCenterProps(item) {
  return {
    id: item.id,
    name: item.name,
    marker: item.marker,
    address: item.address,
    phone: item.phone,
    time: item.time,
    brands: item.brands || [],
    fuel: item.fuel || [],
    services: item.services || [],
    link: item.link || '',
    email: item.email || {},
    socials: item.socials || {},
    button: item.button || {},
    type: TEMPLATE.value || '',
  };
}

function clearMarkers() {
  markers.forEach((m) => m.setMap(null));
  markers = [];
  activeMarkerIndex = -1;
}

function resetActiveMarker() {
  if (activeMarkerIndex !== -1 && markers[activeMarkerIndex]) {
    markers[activeMarkerIndex].setIcon(ICON_DEFAULT);
    activeMarkerIndex = -1;
  }
}
function activateMarker(index) {
  if (!markers.length || index < 0 || index >= markers.length) return;

  if (activeMarkerIndex !== -1 && markers[activeMarkerIndex]) {
    markers[activeMarkerIndex].setIcon(ICON_DEFAULT);
  }
  activeMarkerIndex = index;
  const marker = markers[index];
  marker.setIcon(ICON_ACTIVE);

  unmountInfoCard();

  infoContainer = document.createElement('div');
  const item = centers.value[index];
  const props = getAutoCenterProps(item);
  props['brands-text'] = $t('networkMap.brands');
  props['choose-autocenter-text'] = $t('networkMap.choose_autocenter');
  props['leave-request-text'] = $t('networkMap.leave_request');
  props['fuel-text'] = $t('networkMap.fuel');
  props['services-text'] = $t('networkMap.available_services');

  infoApp = createApp({
    render() {
      return h(MapPointCard, props);
    },
  });
  infoApp._context.components = nuxtApp.vueApp._context.components;

  infoApp._context.directives = nuxtApp.vueApp._context.directives;

  infoApp._context.provides = { ...nuxtApp.vueApp._context.provides };
  infoApp.config.globalProperties = nuxtApp.vueApp.config.globalProperties;

  infoApp.mount(infoContainer);

  infoWindow.setContent(infoContainer);
  infoWindow.open({ anchor: marker, map });

  map.panTo(marker.getPosition());
  if (map.getZoom() < 12) map.setZoom(12);
}

function renderMarkers() {
  if (!map) return;
  clearMarkers();

  const bounds = new window.google.maps.LatLngBounds();

  centers.value.forEach((item, idx) => {
    const lat = Number(item?.marker?.lat);
    const lng = Number(item?.marker?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: ICON_DEFAULT,
      title: item.name || '',
    });

    marker.addListener('click', () => activateMarker(idx));
    markers.push(marker);
    bounds.extend(marker.getPosition());
  });

  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
  }
}

async function tryInitMap(apiKey) {
  try {
    await loadGoogleMaps(apiKey);
    initMap();
    renderMarkers();
  } catch (error) {
    console.error('Failed to load Google Maps with dynamic API key:', error);
  }
}

// watch(...,{immediate:true}) fires synchronously during setup(), before this
// component has had its first mount — mapEl isn't bound yet at that point, and
// nextTick()/rAF don't reliably fix that since they aren't tied to this
// component's own first paint. onMounted() is the point Vue guarantees refs
// are bound, so it handles the initial load; the watcher only reacts to the
// key changing afterwards (e.g. arriving after an async settings fetch).
onMounted(() => {
  if (googleMapsApiKey.value) tryInitMap(googleMapsApiKey.value);
});

watch(
  () => googleMapsApiKey.value,
  (newKey) => {
    if (newKey) tryInitMap(newKey);
  },
);
onBeforeUnmount(() => {
  if (infoWindow) {
    try {
      infoWindow.close();
    } catch {
      console.log('error');
    }
    infoWindow = null;
  }
  clearMarkers();
  map = null;
});
</script>
<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      isCarPage
        ? 'bg-gray'
        : data?.background_color
          ? `bg-${data.background_color}`
          : '',
      isCarPage
        ? 'section--top-zero'
        : data?.spacing?.top
          ? `section--top-${data.spacing.top}`
          : '',
      isCarPage
        ? 'section--bottom-zero'
        : data?.spacing?.bottom
          ? `section--bottom-${data.spacing.bottom}`
          : '',
    ]"
  >
    <div class="container">
      <div
        :class="[
          'network',
          isCarPage
            ? 'network--gray'
            : data?.background_color
              ? `network--${data.background_color}`
              : '',
        ]"
      >
        <h2
          v-if="data.title"
          class="mb-0 network__title h2"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h2>
        <div class="network__info">
          <div class="network__slider">
            <PointsSlider :data="data.items" :type="TEMPLATE" />
          </div>
        </div>
        <div
          ref="mapEl"
          class="network__map"
          :aria-label="data.title"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
        ></div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.network {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 64);
  }
  &--white {
    #{$parent} {
    }
  }
  &--black {
    #{$parent} {
      &__form {
        background-color: rgba($surface-container-opacity, 0.5);
        border: 1px solid $on-surface-variant-2;
      }
      &__checkboxes {
        border-top: 1px solid $on-surface-variant-2;
        @include media-breakpoint-up(md) {
          border-left: 1px solid $on-surface-variant-2;
        }
      }
      &__map {
        background: $lime-green;
      }
    }
  }
  &--gray {
    #{$parent} {
      &__form {
        background-color: $white;
        border: 1px solid $divider;
      }
      &__checkboxes {
        border-top: 1px solid $divider;
        @include media-breakpoint-up(sm) {
          border-top: unset;
        }
        @include media-breakpoint-up(md) {
          border-left: 1px solid $divider;
        }
      }
      &__map {
        background: $lime-green;
      }
    }
  }
  &__title {
    margin: 0;
  }
  &__subtitle {
    max-width: 80%;
    @include media-breakpoint-up(sm) {
      max-width: unset;
    }
  }
  &__search {
    position: relative;
    $parent: &;
    &-field {
      align-items: center;
      display: grid;
      grid-template-columns: auto 16px;
      gap: 12px;
      border-bottom: 1px solid $black;
      grid-template-areas: 'search-input search-icon';
      &--with-value {
        grid-template-areas: 'search-input search-clear';
        #{$parent} {
          &-icon {
            display: none;
          }
        }
      }

      @include media-breakpoint-up(sm) {
        grid-template-columns: 16px auto 16px;
        grid-template-areas: 'search-icon search-input search-input';
        &--with-value {
          grid-template-areas: 'search-icon search-input search-clear';
          #{$parent} {
            &-icon {
              display: block;
            }
          }
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(gap, 12);
        grid-template-columns: px-to-vw(16) auto px-to-vw(16);
      }
    }
    &-icon {
      border: unset;
      grid-area: search-icon;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-el {
        height: 100%;
        width: 100%;
      }
    }
    &-input {
      border-end-end-radius: 0;
      border-start-end-radius: 0;
      border-radius: 0;
      grid-area: search-input;
      width: 100%;
    }
    &-button {
      grid-area: search-clear;
      margin: 0;
      min-width: unset;
      padding: 0;
      position: relative;
      &-icon {
        height: 100%;
        width: 100%;
      }
    }
    &-error {
      background-color: $white;
      border-radius: 4px;
      box-shadow: 0px 4px 36px 0px #00000026;
      left: 0;
      position: absolute;
      padding: 16px;
      width: 100%;
      z-index: 1;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding, 16);
        @include responsive-spacing(border-radius, 4);
      }
    }
  }
  &__regions {
    display: flex;
    flex-direction: column;
    gap: 38px;
    @include media-breakpoint-up(sm) {
      gap: 64px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 64);
    }
    &-top {
      display: grid;
      gap: 24px;
      @include media-breakpoint-up(sm) {
        align-items: center;
        grid-template-columns: repeat(2, 284px);
        justify-content: space-between;
      }
      @include media-breakpoint-up(xl) {
        grid-template-columns: px-to-vw(284) px-to-vw(284);
        @include responsive-spacing(gap, 24);
      }
    }
    &-list {
      display: grid;
      gap: 8px;
      list-style: none;
      @include media-breakpoint-up(sm) {
        grid-template-columns: repeat(2, 1fr);
      }
      @include media-breakpoint-up(md) {
        grid-template-columns: repeat(3, 1fr);
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
    }
  }
  &__form {
    border-radius: 4px;

    display: grid;
    grid-template-columns: 1fr;
    @include media-breakpoint-up(md) {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  &__floatlabel {
  }
  &__checkboxes {
    display: grid;
    gap: 4px;
    grid-template-columns: 1fr;
    padding: 16px;
    @include media-breakpoint-up(sm) {
      grid-template-columns: repeat(2, 1fr);
      padding: 24px;
    }
    @include media-breakpoint-up(md) {
      border-top: unset;
      column-gap: 40px;
      grid-template-columns: repeat(2, 1fr);
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-left-width, 1);
      @include responsive-spacing(column-gap, 40);
      @include responsive-spacing(padding, 24);
    }
  }
  &__checkbox {
  }
  &__info {
    display: flex;
    flex-direction: column;
    gap: 32px;
    @include media-breakpoint-up(sm) {
      gap: 48px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 48);
    }
  }
  &__map {
    width: 100%;
    height: 620px;
    border-radius: 8px;
    overflow: hidden;
    @include media-breakpoint-up(sm) {
      height: 840px;
    }
    @include media-breakpoint-up(md) {
      height: 600px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(height, 600);
      @include responsive-spacing(border-radius, 8);
    }
    &:deep(.gm-style-iw) {
      padding: 0;
      max-height: 493px !important;
      max-width: 312px !important;
      min-width: 296px !important;
      @include media-breakpoint-up(sm) {
        max-height: 479px !important;
        max-width: 384px !important;
        min-width: 384px !important;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(max-width, 384, true);
        @include responsive-spacing(min-width, 384, true);
        @include responsive-spacing(max-height, 479, true);
      }
    }
    &:deep(.gm-style-iw-chr) {
      position: absolute;
      right: 16px;
      top: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(right, 16);
        @include responsive-spacing(top, 16);
      }
    }
    &:deep(.gm-style-iw-chr button) {
      height: 32px !important;
      opacity: 1;
      width: 32px !important;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 32, true);
        @include responsive-spacing(width, 32, true);
      }
    }
    &:deep(.gm-style-iw-chr button span) {
      background: url('/assets/icons/close-black.svg') no-repeat -1px -1px;
      background-size: contain;
      background-color: transparent !important;
      height: 16px !important;
      margin: auto !important;
      mask-image: unset !important;
      width: 16px !important;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 16, true);
        @include responsive-spacing(width, 16, true);
      }
    }
    &:deep(.gm-style-iw-ch) {
      display: none;
    }
  }
}
</style>
